export interface CartItem {
  id: string;
  service_id: string;
  service_name?: string;
  provider_id?: string;
  provider_name?: string;
  quantity: number;
  unit_price_cents: number;
  term_months: number;
  price?: number;
  status: 'ready' | 'pending' | 'confirmed' | 'unavailable' | 'expired' | 'saved_for_later';
  product_class?: 'A' | 'B' | 'C';
  added_catalog_instance_id?: string;
  service_address?: string;
  selected_address_id?: string;
  selected_address_label?: string;
  selected_address_full?: {
    street_address: string;
    city: string;
    state: string;
    zip_code: string;
    country: string;
  };
  perk_info?: string;
  selected_perk_id?: string;
  selected_perk_name?: string;
  service_perks?: any[];
  is_selected?: boolean;
  variant_key?: string;
  // Variant-related fields
  variant_selections?: Record<string, string>;
  service_configuration?: {
    additional_users?: number;
    recurring_bolt_ons?: Array<{ addon_key: string }>;
    one_time_addons?: Array<{ addon_key: string; quantity: number }>;
    [key: string]: any;
  };
  variant_price?: number | null;
  order_schema?: Record<string, any>;
  pricing_info?: {
    recurring_cents?: number;
    min_recurring_cents?: number;
    max_recurring_cents?: number;
    frequency?: string;
    additional_users?: number;
    additional_user_price_cents?: number;
    additional_user_total_cents?: number;
    included_users?: number;
    quantity_unit_label?: string;
    quantity_max?: number | null;
    recurring_bolt_ons?: Array<{
      addon_key: string;
      name: string;
      description?: string;
      group?: string;
      type?: string;
      price_cents: number;
      total_price_cents: number;
    }>;
    recurring_bolt_ons_total_cents?: number;
    one_time_addons?: Array<{
      addon_key: string;
      name: string;
      description?: string;
      group?: string;
      type?: string;
      quantity: number;
      price_cents: number;
      total_price_cents: number;
      max_per_order: number;
    }>;
    one_time_addons_total_cents?: number;
    one_time_total_cents?: number;
    selected_variant?: {
      key?: string;
      display_name?: string;
      description?: string | null;
    };
  };
}

/**
 * Get the exact effective recurring price for a cart item.
 * Prefer the server-resolved configured recurring price whenever available.
 */
export function getEffectivePriceCents(item: CartItem): number {
  if (typeof item.pricing_info?.recurring_cents === 'number') {
    return item.pricing_info.recurring_cents;
  }
  if (typeof item.unit_price_cents === 'number') {
    return item.unit_price_cents;
  }
  if (typeof item.pricing_info?.min_recurring_cents === 'number') {
    return item.pricing_info.min_recurring_cents;
  }
  return 0;
}

export function getPriceDisplay(item: CartItem): string {
  if (typeof item.pricing_info?.recurring_cents === 'number') {
    return `$${(item.pricing_info.recurring_cents / 100).toFixed(2)}`;
  }

  if (
    typeof item.pricing_info?.min_recurring_cents === 'number' &&
    typeof item.pricing_info?.max_recurring_cents === 'number'
  ) {
    return `$${(item.pricing_info.min_recurring_cents / 100).toFixed(2)} - $${(
      item.pricing_info.max_recurring_cents / 100
    ).toFixed(2)}`;
  }

  return `$${(getEffectivePriceCents(item) / 100).toFixed(2)}`;
}

export function getCartRecurringSubtotalCents(items: CartItem[] = []): number {
  return items.reduce((total, item) => total + getEffectivePriceCents(item) * item.quantity, 0);
}

export function getCartOneTimeSubtotalCents(items: CartItem[] = []): number {
  return items.reduce(
    (total, item) => total + (Number(item.pricing_info?.one_time_total_cents || 0) * item.quantity),
    0,
  );
}

export interface Cart {
  id: string;
  items: CartItem[];
  ready_items: CartItem[];
  pending_items: CartItem[];
  address_id?: string;
  attribution_breakdown?: Record<string, number>;
  total_items?: number;
  total_value_cents?: number;
}

export interface CartItemProps {
  item: CartItem;
  onUpdateQuantity: (id: string, quantity: number) => void;
  onRemove: (id: string) => void;
  onSaveForLater: (id: string) => void;
  onMoveToCart: (id: string) => void;
  onToggleSelection: (id: string, selected: boolean) => void;
  onViewDetails: (serviceId: string, item?: CartItem) => void;
  onEditVariants: (serviceId: string, item: CartItem) => void;
}

export interface SavedForLaterItemProps {
  item: CartItem;
  onUpdateQuantity: (id: string, quantity: number) => void;
  onRemove: (id: string) => void;
  onMoveToCart: (id: string) => void;
}

export interface CartCheckoutSectionProps {
  cart: Cart;
  onCheckout: () => void;
  canCheckout: boolean;
  getReadyItemsCount: () => number;
  getTotalItems: () => number;
}

export interface CommissionAttributionSectionProps {
  cart: Cart;
}

export interface ServiceDetailModalProps {
  open: boolean;
  service: any;
  loading: boolean;
  cartItem?: CartItem;
  onClose: () => void;
}

export interface VariantEditModalProps {
  open: boolean;
  service: any;
  cartItem?: CartItem;
  selectedVariantKey: string;
  additionalUsers: number;
  recurringBoltOns: Array<{ addon_key: string }>;
  saving: boolean;
  onClose: () => void;
  onSave: () => void;
  onVariantChange: (variantKey: string) => void;
  onAdditionalUsersChange: (value: number) => void;
  onRecurringBoltOnsChange: (value: Array<{ addon_key: string }>) => void;
}
